import { injectable, inject } from 'inversify';
import type { IHttpService } from './modules/http';
import { TYPES } from './modules/http';
import type { BlobInfo } from './CaseDataProvider';

export interface FileInfo {
  descriptor: string;
  url: string;
  expiration: string;
  md5?: string;
}

export interface NewPackageFileCreateInfo {
  descriptor: string;
  fileName?: string;
  md5?: string;
}

export interface NewPackageFileUploadInfo {
  descriptor: string;
  url: string;
}

export interface NewPackageInfo {
  packageId: string;
  uploadUrls: Array<NewPackageFileUploadInfo>;
}

@injectable()
export class CaseFilesApi {
  private _http: IHttpService;

  public constructor(@inject(TYPES.HttpService) http: IHttpService) {
    this._http = http;
  }

  public async getCaseFilesList(packageId: string): Promise<Array<BlobInfo>> {
    const res = await this._http.get<Array<FileInfo>>('CaseFilesInfo/' + packageId);
    return res.data.map((fileInfo) => {
      return {
        ...fileInfo,
        expiration: Date.parse(fileInfo.expiration)
      };
    });
  }

  public async getCaseFileInfo(packageId: string, fileDescriptor: string): Promise<BlobInfo> {
    const res = await this._http.get<FileInfo>('CaseFileInfo/' + packageId + '/' + fileDescriptor);
    return {
      ...res.data,
      expiration: Date.parse(res.data.expiration)
    };
  }

  public async derivePackage(
    initialPackageId: string,
    files: Array<NewPackageFileCreateInfo>
  ): Promise<NewPackageInfo> {
    const res = await this._http.post<NewPackageInfo>('CreatePackage/derive/' + initialPackageId, files);
    return res.data;
  }

  public async saveDerivedPackage(caseId: string, packageId: string, initialPackageId: string): Promise<void> {
    await this._http.put('CreatePackage/save/' + caseId + '/' + packageId + '/' + initialPackageId).catch((error) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      alert(error?.response?.data?.exception);
    });
  }

  public async approveDerivedPackage(caseId: string, packageId: string, initialPackageId: string): Promise<string> {
    const res = await this._http.post<string>(
      'CreatePackage/approve/' + caseId + '/' + packageId + '/' + initialPackageId
    );
    return res.data;
  }

  public async rejectDerivedPackage(caseId: string, packageId: string, initialPackageId: string): Promise<string> {
    const res = await this._http.post<string>(
      'CreatePackage/reject/' + caseId + '/' + packageId + '/' + initialPackageId
    );
    return res.data;
  }
}
